import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	forwardRef,
	Input,
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";

/**
 * Slider (rango) del design-system `.ds`, sin Material. `<input type="range">` estilado dentro
 * del patrón `.ff-field`, con lectura del valor. `ControlValueAccessor` → `formControlName` /
 * `[(ngModel)]`. Emite number. Sustituye a `mat-slider`.
 */
@Component({
	selector: "app-ds-slider",
	standalone: true,
	imports: [CommonModule, FormsModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => DsSliderComponent),
			multi: true,
		},
	],
	template: `
		<div class="ff-field">
			<label *ngIf="label">{{ label }}<span class="req" *ngIf="required">*</span></label>
			<div class="ds-slider-row">
				<input
					type="range"
					class="ds-slider"
					[min]="min"
					[max]="max"
					[step]="step"
					[disabled]="disabled"
					[ngModel]="value"
					(ngModelChange)="onInput($event)"
					(blur)="onTouched()"
				/>
				<span class="ds-slider-val">{{ value }}{{ suffix }}</span>
			</div>
			<div class="ff-hint" *ngIf="hint">{{ hint }}</div>
		</div>
	`,
	styles: [
		`
			:host {
				display: block;
			}
			.ds-slider-row {
				display: flex;
				align-items: center;
				gap: 12px;
			}
			.ds-slider {
				flex: 1;
				min-width: 0;
				accent-color: var(--accent);
				cursor: pointer;
			}
			.ds-slider:disabled {
				cursor: not-allowed;
				opacity: 0.6;
			}
			.ds-slider-val {
				flex: none;
				min-width: 44px;
				text-align: right;
				font: 600 14px var(--mono, monospace);
				color: var(--ink);
			}
		`,
	],
})
export class DsSliderComponent implements ControlValueAccessor {
	@Input() label = "";
	@Input() hint = "";
	@Input() suffix = "";
	@Input() required = false;
	@Input() disabled = false;
	@Input() min = 0;
	@Input() max = 100;
	@Input() step = 1;

	value = 0;

	private onChange = (_value: number): void => {
		// intentional
	};
	onTouched = (): void => {
		// intentional
	};

	constructor(private readonly cdr: ChangeDetectorRef) {}

	writeValue(value: number): void {
		this.value = value ?? 0;
		this.cdr.markForCheck();
	}

	registerOnChange(fn: (value: number) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdr.markForCheck();
	}

	onInput(value: number | string): void {
		this.value = Number(value);
		this.onChange(this.value);
	}
}
